import { DependencyList, useCallback } from 'react'
import { CancelToken } from 'axios'
import { ApiAccess } from 'features/session/types'
import {
  ApiGetWithToken,
	ApiPatchWithToken,
	useApiFetch,
	useApiFetchList,
	useApiPost,
} from 'shared/api'
import {
	searchsAuthorApi,
	searchsAuthorApiBy,
	searchsSisetuApiBy,
	sisetuApiBy,
	targetListsAuthorApiBy
} from 'interface/endpoints'
import {
	AuthorResult,
	AuthorResultList,
	EditAuthorInfoFormData,
	AuthorLinkResultList,
	AuthrosMeshResultList,
	CollaborateAuthorResultList,
	AuthorsAffiliationResultList
} from './types'
import { KojinResult } from 'interface/kojin'
import { useUpdateTime } from 'interface/util'

export * from './types'

export const useListAuthor = (
	searchId: number | undefined | null,
	sisetuCode: string,
	mesh: string | undefined,
	option: {
		updateTime?: string
		partialLimit?: number
		onPreFetch?: () => void
		onFetched?: (result: AuthorResult[]) => void
	} = {},
	deps?: DependencyList
) => {
	const { updateTime, partialLimit, onPreFetch, onFetched } = option
	const t = useUpdateTime(updateTime)
	return useApiFetchList<AuthorResult, AuthorResult[]>(
		useCallback(
			(apiAccess, cancelToken) => {
				if (onPreFetch) onPreFetch()
				return ApiGetWithToken<number>(
					apiAccess,
					searchId ? (
						searchsSisetuApiBy(searchId, sisetuCode, '/author/count', { t, mesh })
					) : (
						sisetuApiBy(sisetuCode, '/author/count', { t, mesh })
					),
					cancelToken
				)
			},
			[t, searchId, sisetuCode, mesh, onPreFetch]
		),
		useCallback(
			(apiAccess, cancelToken, offset, limit) => {
				const wrapper = async () => {
					const list = await ApiGetWithToken<AuthorResultList>(
						apiAccess,
						searchId ? (
							searchsSisetuApiBy(searchId, sisetuCode, '/author/list', { t, mesh, offset, limit })
						) : (
							sisetuApiBy(sisetuCode, '/article/list', { t, mesh, offset, limit })
						),
						cancelToken
					)
					return list?.list
				}
				return wrapper()
			},
			[t, searchId, sisetuCode, mesh]
		),
		useCallback(
			(result) => {
				if (onFetched) onFetched(result)
				return result
			},
			[onFetched]
		),
		partialLimit ?? 1000,
		deps
	)
}

export const useGetAuthor = (
	searchId: number | null | undefined,
	authorId: number | null | undefined,
	option: {
		defaultAuthor?: AuthorResult
		updateTime?: string
	} = {}
) => {
	const { defaultAuthor, updateTime } = option
	const t = useUpdateTime(updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !authorId) return null
				if (defaultAuthor) return defaultAuthor
				return GetAuthor(apiAccess, searchId, authorId, t, cancelToken)
			},
			[t, searchId, authorId, defaultAuthor]
		)
	)
}
export const GetAuthor = (
	apiAccess: ApiAccess,
	searchId: number,
	authorId: number,
	t?: string,
	cancelToken?: CancelToken
) => ApiGetWithToken<AuthorResult>(
	apiAccess,
	searchsAuthorApiBy(searchId, authorId, undefined, { t }),
	cancelToken
)

export const useListAuthorsAffiliation = (
	searchId: number | null | undefined,
	authorId: number | null | undefined,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !authorId) return null
				return ApiGetWithToken<AuthorsAffiliationResultList>(
					apiAccess,
					searchsAuthorApiBy(searchId, authorId, '/affiliation', { t }),
					cancelToken
				)
			},
			[t, searchId, authorId]
		)
	)
}

export const useListAuthorsCollaborator = (
	searchId: number | null | undefined,
	authorId: number | null | undefined,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId || !authorId) return null
				return ApiGetWithToken<CollaborateAuthorResultList>(
					apiAccess,
					searchsAuthorApiBy(searchId, authorId, '/collabo', { t }),
					cancelToken
				)
			},
			[t, searchId, authorId]
		)
	)
}

export const useEditAuthor = (
	searchId?: number | null,
	targetListId?: number | null,
	authorId?: number | null, 
  callback?: {
    onEditted?: (result: KojinResult) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError?: (reason: any) => void
  }
) => {
	return useApiPost<EditAuthorInfoFormData, KojinResult>(
		useCallback(
			(apiAccess, cancelToken, form, webSocketId) => {
				if (!searchId || !authorId) return null
				return ApiPatchWithToken(
					apiAccess,
					targetListId ? targetListsAuthorApiBy(searchId, targetListId, authorId) :
						searchsAuthorApiBy(searchId, authorId),
					form,
					cancelToken,
					webSocketId
				)
			},
			[searchId, authorId, targetListId]
		),
		useCallback(
			(result) => {
				if (callback?.onEditted) callback.onEditted(result)
				return result
			},
			[callback]
		),
		useCallback(
			(reason) => {
				if (callback?.onError) callback?.onError(reason)
			},
			[callback]
		)
	)
}

export const useListAuthorLinks = (
	searchId: number | undefined | null,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId) return null
				return ApiGetWithToken<AuthorLinkResultList>(
					apiAccess,
					searchsAuthorApi(searchId, '/links', { t }),
					cancelToken
				)
			},
			[t, searchId]
		)
	)
}

export const useListMesh = (
	searchId: number | null | undefined,
	sisetuCode: string | null | undefined,
	authorId: number | null | undefined,
	option: {
		updateTime?: string
	} = {}
) => {
	const t = useUpdateTime(option.updateTime)
	return useApiFetch(
		useCallback(
			(apiAccess, cancelToken) => {
				if (!searchId) return null
				if (authorId) {
					return ApiGetWithToken<AuthrosMeshResultList>(
						apiAccess,
						searchsAuthorApiBy(searchId, authorId, '/mesh/list', { t }),
						cancelToken
					)
				} else if (sisetuCode) {
					return ApiGetWithToken<AuthrosMeshResultList>(
						apiAccess,
						searchsSisetuApiBy(searchId, sisetuCode, '/mesh/list', { t }),
						cancelToken
					)
				}
				return null
			},
			[t, searchId, sisetuCode, authorId]
		)
	)
}
